import React from 'react';
import { Box } from '../../base/Box/Box';
import { Type } from '../../base/Type/Type';
import Markup from '../../base/Markup/Markup';
import Link from '../../base/Link/Link';
import { HeaderHeroSectionStoryblok } from '../../types/storyblok';
import renderStoryblokComponents from '../../utils/renderStoryblokComponents';
import classNames from 'classnames';
import { StoryblokImage } from '../../base/StoryblokImage/StoryblokImage';
import { BackgroundImage } from './BackgroundImage';

type Stat = { description: string; stat: string } & object;

const isActiveStat = ({ description, stat }: Stat) => {
  const descriptionNotEmpty = description !== '';
  const statNotEmpty = stat !== '';

  return descriptionNotEmpty && statNotEmpty;
};

export const HeaderHeroSection = ({
  subheading,
  heading,
  description,
  stats = [],
  linkTitle,
  link,
  colourScheme = 'white',
  overlayOverBkg,
  logo,
  desktopBackgroundImage,
  mobileBackgroundImage,
}: HeaderHeroSectionStoryblok) => {
  if (
    !subheading &&
    !heading &&
    !description &&
    !stats &&
    !linkTitle &&
    !link &&
    !logo
  ) {
    return null;
  }

  const activeStats = stats.filter(isActiveStat);
  const hasStats = !!activeStats.length;
  const hasLogo = !!logo && !!logo.filename;
  const hasDesktopBackgroundImage =
    !!desktopBackgroundImage && !!desktopBackgroundImage.filename;
  const hasMobileBackgroundImage =
    !!mobileBackgroundImage && !!mobileBackgroundImage.filename;

  return (
    <Box
      as="section"
      colourScheme={colourScheme || 'white'}
      blockSpacing
      bkg
      overlayOverBkg={overlayOverBkg}
    >
      <Box as="div" className="mx-auto max-w-standard" overlayOverBkg>
        {hasLogo ? (
          <StoryblokImage
            src={logo}
            maxWidth={200}
            alt="Header Hero logo"
            mode="responsive"
            className="mb-5"
          />
        ) : null}
        {hasDesktopBackgroundImage ? (
          <BackgroundImage
            image={desktopBackgroundImage}
            colourScheme={colourScheme}
          />
        ) : null}
        {hasMobileBackgroundImage ? (
          <BackgroundImage
            image={mobileBackgroundImage}
            isMobile
            colourScheme={colourScheme}
          />
        ) : null}
        <Type as="h3" typography="subheading">
          {subheading}
        </Type>
        <Type
          as="h1"
          className={classNames('font-helvetica text-h1', hasStats && 'mb-5')}
        >
          {heading}
        </Type>
        <Markup content={description} className="text-p1" />
        {hasStats ? (
          <Box
            as="ul"
            className="align-center mt-5 inline-flex flex-wrap gap-6 rounded-[0.25em] px-[0.75em] py-[0.5em]"
            highlightBkg
            overlayOverBkg
          >
            {renderStoryblokComponents({ blocks: activeStats })}
          </Box>
        ) : null}
        <Box as="div">
          <Link href={link} variant="primary" className="mt-12">
            {linkTitle}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default HeaderHeroSection;
